import { Application, Container, Graphics } from "pixi.js";
import { DEMO_FORM } from "@talent/tasks";
import type { TaskDefinition } from "@talent/protocol";
import {
  SessionRecorder,
  TouchTracker,
  detectImplausibleSpeed,
  detectMultiTouch,
} from "@talent/client-telemetry";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilAbandoned,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { COLOR, familyColor, lighten, mix } from "./paint.js";

/**
 * Build the same shape — the constellation game.
 *
 * A target pattern sits above; the child places stars on an empty sky below to match it. The target
 * stays VISIBLE throughout, which is what makes this construction rather than memory, and it is the
 * point: this is one of the few activities where a child MAKES something instead of choosing or
 * repeating.
 *
 * **This renderer briefly served two games and now serves one.** The mirror game ran on it through
 * an `expectedResponse` hook that folded the target, plus a flag that drew a dashed line between
 * the panels — the same shape as `span-task.ts` serving forward and backward recall, and defensible
 * on those grounds. It was still the wrong call: sharing the renderer is exactly why the two games
 * were indistinguishable on screen, and the mirror game is a butterfly now with a renderer of its
 * own (`mirror-task.ts`). The hook and the fold line went with it rather than staying here as
 * generality with no second caller.
 *
 * **No confirm button.** The trial resolves when the child has placed as many stars as the target
 * has. A "done" button would be text on a screen that may not contain text (v2 §15), and a
 * six-year-old should not have to find a second control to finish a thing they can see is
 * finished. Tapping a placed star removes it, so a mistake is recoverable without a reset — and
 * every removal is recorded, which is exactly the process signal that a bare right/wrong throws
 * away.
 *
 * Scored on accuracy, and deliberately outside the working-memory span estimate.
 */

export interface BuildTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateTarget: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => number[];
  scoreTrial: (target: readonly number[], placed: readonly number[]) => boolean;
  /** Play the wordless demonstration first. Set on a child's first time with this game. */
  showTutorial?: boolean;
  /** Awaited after the demonstration, so the shell can hold with a real dialog. */
  /**
   * Awaited after the demonstration. Resolving with "replay" runs it again.
   *
   * A demonstration that plays once at a speed somebody else chose is lost for good to a child who
   * blinked, and their alternative is guessing through a game whose rule they never saw.
   */
  onTutorialComplete?: () => Promise<"replay" | "start">;
  /** Aborted when the child leaves the game, so the task closes rather than hanging. */
  signal?: AbortSignal;
  /**
   * The level to open at, from the server's suggestion for this child.
   *
   * Absent for free play, which opens at the game's own floor. Clamped into range rather than
   * trusted: it is derived from an objective's level window, which lives in a different file from
   * this task's ceiling.
   */
  startAt?: number;
}

export async function runBuildTask(opts: RunOptions): Promise<BuildTaskResult> {
  const { task, recorder, formId } = opts;
  const cols = Number(task.params["gridCols"]);
  const rows = Number(task.params["gridRows"]);
  const cellCount = cols * rows;
  const accent = familyColor(task.family);
  /**
   * Every sleep inside this task is interruptible.
   *
   * Aliased once rather than threading the signal through a dozen call sites: a task that keeps
   * animating after the child has left is a task whose canvas is still on screen while the next
   * one is being built on top of it.
   */
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  // The play area is shared between games, and it must contain exactly one canvas. The route
  // effect already waits for the previous run to tear down, so this should be a no-op — it is
  // here because two stacked canvases both keep their pointer handlers, which presents as taps
  // going to the wrong game and is very hard to see.
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  /** Fixed stars scattered on the ground, so the sky is a sky and not a dark rectangle. */
  const speckles: { x: number; y: number; r: number; a: number }[] = [];
  for (let i = 0; i < 46; i++) {
    speckles.push({
      x: Math.random(),
      y: Math.random(),
      r: 0.6 + Math.random() * 1.5,
      a: 0.18 + Math.random() * 0.4,
    });
  }

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    const bands = 24;
    for (let i = 0; i < bands; i++) {
      backdrop
        .rect(0, (h / bands) * i, w, h / bands + 1)
        .fill(mix(COLOR.stage, COLOR.stageDeep, i / bands));
    }
    for (const s of speckles) {
      backdrop.circle(s.x * w, s.y * h, s.r).fill({ color: 0xffffff, alpha: s.a });
    }
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  /** Draws a grid panel. `placeable` gives the empty sky a visible frame to build inside. */
  function drawPanel(
    g: Graphics,
    filled: ReadonlySet<number>,
    size: number,
    opts2: { placeable: boolean; state?: "idle" | "right" | "wrong" },
  ): void {
    const state = opts2.state ?? "idle";
    const panel =
      state === "right"
        ? mix(COLOR.correct, COLOR.stage, 0.6)
        : state === "wrong"
          ? mix(COLOR.wrong, COLOR.stage, 0.6)
          : mix(COLOR.stage, COLOR.card, opts2.placeable ? 0.1 : 0.16);
    const star = state === "right" ? COLOR.correct : state === "wrong" ? COLOR.wrong : accent;

    g.clear();
    g.roundRect(-size / 2, -size / 2, size, size, size * 0.16).fill(panel);

    const pad = size * 0.12;
    const inner = size - pad * 2;
    const cell = inner / cols;

    for (let i = 0; i < cellCount; i++) {
      const cx = -size / 2 + pad + (i % cols) * cell + cell / 2;
      const cy = -size / 2 + pad + Math.floor(i / cols) * cell + cell / 2;
      if (filled.has(i)) {
        drawStar(g, cx, cy, cell * 0.34, star);
      } else if (opts2.placeable) {
        // A faint well marks where a star can go. Without it a child cannot tell an empty sky
        // from a decorative panel.
        g.circle(cx, cy, cell * 0.13).fill({ color: 0xffffff, alpha: 0.16 });
      }
    }
  }

  /** A five-pointed star. Recognisable at 20 px, which a circle is not. */
  function drawStar(g: Graphics, cx: number, cy: number, r: number, color: number): void {
    const points: number[] = [];
    for (let i = 0; i < 10; i++) {
      const angle = (Math.PI / 5) * i - Math.PI / 2;
      const radius = i % 2 === 0 ? r : r * 0.44;
      points.push(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius);
    }
    g.poly(points).fill(color);
    g.circle(cx - r * 0.18, cy - r * 0.2, r * 0.2).fill({ color: 0xffffff, alpha: 0.5 });
  }

  /**
   * Runs one trial and resolves with the cells the child placed, in the order placed.
   *
   * Order is kept even though scoring ignores it: how a child assembles a shape — corners first,
   * left to right, or wandering — is process data, and it is only available if the sequence is
   * recorded rather than the finished set.
   */
  function present(target: readonly number[]): {
    placed: Promise<number[]>;
    simulateTap: (cell: number) => void;
    removals: () => number;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);

    const targetSet = new Set(target);
    const placedSet = new Set<number>();
    let removals = 0;

    const targetPanel = new Graphics();
    const board = new Graphics();
    board.eventMode = "static";
    board.cursor = "pointer";
    layer.addChild(targetPanel, board);

    /** Current geometry. Read by hit-testing, so it must never be a stale copy. */
    let targetSize = 0;
    let boardSize = 0;
    let boardState: "idle" | "right" | "wrong" = "idle";

    /**
     * Positions and redraws everything from the CURRENT canvas size.
     *
     * Called on resize as well as at start. On a phone the visual viewport changes whenever the
     * address bar slides away, and the first version drew once from the size at trial start — so
     * after any such change the panels were positioned for a screen that no longer existed and
     * taps landed on the wrong cells.
     */
    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const pad = Math.max(12, w * 0.05);
      // Capped absolutely as well as relatively, or on a tablet the panels become enormous.
      targetSize = Math.min(w * 0.42, h * 0.24, 190);
      // The board is what gets touched, so it takes the larger share.
      boardSize = Math.min(w - pad * 2, h - targetSize - pad * 4, 380);

      const totalH = targetSize + boardSize + pad * 2;
      const top = Math.max(pad, (h - totalH) / 2);

      drawPanel(targetPanel, targetSet, targetSize, { placeable: false });
      targetPanel.position.set(w / 2, top + targetSize / 2);

      drawPanel(board, placedSet, boardSize, { placeable: true, state: boardState });
      board.position.set(w / 2, top + targetSize + pad * 2 + boardSize / 2);

    }

    layout();
    app.renderer.on("resize", layout);
    glow(glowLayer, targetPanel.position.x, targetPanel.position.y, targetSize * 0.6, accent, 900);

    if (!prefersReducedMotion()) {
      targetPanel.scale.set(0);
      board.scale.set(0);
      void animate(340, (t) => targetPanel.scale.set(easeOutBack(t)));
      void idle(120).then(() => animate(340, (t) => board.scale.set(easeOutBack(t))));
    }

    let resolvePlaced: ((cells: number[]) => void) | null = null;
    let accepting = true;
    const order: number[] = [];

    /** Which cell a local point falls in, or null if it missed the grid. */
    const cellAt = (lx: number, ly: number): number | null => {
      const pad = boardSize * 0.12;
      const inner = boardSize - pad * 2;
      const cell = inner / cols;
      const gx = lx + boardSize / 2 - pad;
      const gy = ly + boardSize / 2 - pad;
      if (gx < 0 || gy < 0 || gx > inner || gy > inner) return null;
      const c = Math.min(cols - 1, Math.floor(gx / cell));
      const r = Math.min(rows - 1, Math.floor(gy / cell));
      return r * cols + c;
    };

    /** One placement or removal. Shared by the pointer handler and the demonstration, so a
     *  tutorial cannot drift from the game it teaches. */
    function tapCell(index: number): void {
      if (!accepting) return;

      const pad = boardSize * 0.12;
      const cell = (boardSize - pad * 2) / cols;
      const cx = board.position.x - boardSize / 2 + pad + (index % cols) * cell + cell / 2;
      const cy = board.position.y - boardSize / 2 + pad + Math.floor(index / cols) * cell + cell / 2;

      if (placedSet.has(index)) {
        // Tapping a placed star removes it. Recoverable without a reset, and each removal is a
        // recorded signal about how the child worked rather than a wasted action.
        placedSet.delete(index);
        removals++;
        tick();
        ripple(fx, cx, cy, COLOR.card, cell * 0.9);
      } else {
        placedSet.add(index);
        order.push(index);
        cellTone(index);
        ripple(fx, cx, cy, accent, cell);
        glow(glowLayer, cx, cy, cell * 0.5, accent, 340);
      }
      layout();

      // Resolves when the count matches the ANSWER, not the panel above. Folding preserves the
      // cell count so the two agree today, and relying on that would be a trap for the next
      // transformation somebody adds. No confirm button: text is not allowed on this surface, and
      // a finished shape is visibly finished.
      if (placedSet.size === targetSet.size) {
        accepting = false;
        void idle(220).then(() => resolvePlaced?.([...order.filter((c) => placedSet.has(c))]));
      }
    }

    board.on("pointertap", (event) => {
      const local = board.toLocal(event.global);
      const index = cellAt(local.x, local.y);
      if (index !== null) tapCell(index);
    });

    const placed = new Promise<number[]>((resolve) => {
      resolvePlaced = resolve;
    }).then(async (cells) => {
      const correct = opts.scoreTrial([...targetSet], cells);
      if (correct) {
        successChime();
        boardState = "right";
        layout();
        burst(fx, board.position.x, board.position.y, [
          accent,
          lighten(accent, 0.45),
          COLOR.correct,
        ], 22);
        await pop(board, { scale: 1.08 });
        await idle(420);
      } else {
        missTone();
        boardState = "wrong";
        layout();
        await nudge(board);
        await idle(220);
        // Show the answer on the child's own board, so the correction lands where they built. The
        // ANSWER — for the mirror game the panel above is not it, and showing the target here would
        // teach the child that copying was right.
        placedSet.clear();
        for (const cell of targetSet) placedSet.add(cell);
        boardState = "right";
        layout();
        glow(glowLayer, board.position.x, board.position.y, boardSize * 0.5, COLOR.correct, 700);
        await idle(760);
      }
      return cells;
    });

    return {
      placed,
      /** Lets the demonstration place a star itself. */
      simulateTap: tapCell,
      removals: () => removals,
      cleanup: () => {
        accepting = false;
        app.renderer.off("resize", layout);
        layer.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Trial loop
  // ---------------------------------------------------------------------------
  // The level to open at comes from the server's suggestion — the objective the child is working
  // on — and is clamped into this task's range by `newLadder`.
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: BuildTaskResult["reason"] = "completed";

  /**
   * A wordless demonstration, before anything is scored.
   *
   * Two things are invisible in this game. That the lower grid is something you BUILD on rather
   * than another picture to look at, and that the trial ends by itself once the star count matches
   * — there is no button to press, which is right for a surface with no text (v2 §15) and leaves a
   * child with nothing to tell them they have finished.
   *
   * So the game builds one shape itself: stars land one at a time, the count reaches the target,
   * and the trial resolves. Both facts, shown in a few seconds.
   *
   * Its own target under a `demo` form id, never a scored trial, and not recorded — every child
   * sees the same demonstration, so it says nothing about any of them.
   */
  async function playTutorial(): Promise<void> {
    // Looped: the child can watch it again as many times as they want.
    let choice: "replay" | "start" = "start";
    do {
      const target = opts.generateTarget(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const shown = present(target);
      // A beat to see the shape being worked from, before anything starts appearing below.
      await idle(950);
      for (const cell of target) {
        if (opts.signal?.aborted) break;
        shown.simulateTap(cell);
        // Slow enough to read as placing one star at a time rather than a shape appearing at once.
        await idle(480);
      }
      if (!opts.signal?.aborted) {
        await untilAbandoned(shown.placed, opts.signal);
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const target = opts.generateTarget(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", { trialIndex, difficulty, stimulus: target });

      const start = performance.now();
      const trial = present(target);
      const outcome = await untilSettled(trial.placed, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      const removals = trial.removals();
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        /**
         * Stopping and leaving both end the sitting, and only one of them is recorded.
         *
         * A timeout means the child put the phone down mid-trial. It is `abandoned` rather than a
         * wrong answer on purpose: we know they stopped and we do not know they could not do it, and
         * scoring silence as failure is the mistake `stopAfterErrors` used to make.
         */
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }
      const placed = outcome;

      const correct = opts.scoreTrial(target, placed);
      if (correct) correctCount++;

      await recorder.record("trial_response", {
        trialIndex,
        // The order stars were placed in, not the finished set: how a child assembles a shape is
        // process data, and it is unrecoverable from the result.
        response: placed,
        correct,
        responseMs: elapsed,
        // Self-correction: a first-class signal that a right/wrong score discards. Two children
        // can both finish the shape while one built it straight through and the other rearranged
        // it four times.
        corrections: removals,
      });

      const fast = detectImplausibleSpeed(elapsed, target.length);
      if (fast) await recorder.record("input_anomaly", fast);
      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      /**
       * The teaching policy lives in `ladder.ts`, not here.
       *
       * A streak raises the level, errors lower it, and the sitting ends when the trials run out —
       * never because a child was struggling (R-5). It was copied into all ten renderers before,
       * which is nine chances for one game to end up quietly stricter than the rest.
       */
      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(260);
    }
  } catch {
    reason = "error";
  } finally {
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
